/**
 * Shared option lists for the Register Server / Register Tool forms (F032 MCP Governance),
 * consumed by both the form components and `McpServerRegister.schema.ts` /
 * `McpToolRegister.schema.ts`.
 *
 * SUPERSEDED (Charter M3 migration): the `VS-`/`VT-` validator functions below and
 * `mapServerErrorToFormState` are no longer wired into `McpServerRegisterForm.tsx` /
 * `McpToolRegisterForm.tsx` — those now validate via the imported Zod schemas
 * (`react-hook-form` + `zodResolver`) and map server errors via the shared
 * `src/components/forms/applyServerErrors.ts`. This module is kept, unchanged, so
 * `mcpServerFormFields.test.ts` (the per-rule regression tests predating the RHF migration)
 * keeps passing, and because the enum option lists (`MCP_ACCESS_LEVEL_OPTIONS`,
 * `DATA_SENSITIVITY_OPTIONS`) are still the schema's source of truth. See `McpServerRegister
 * .schema.ts` for the live validators (same rule IDs, same verbatim messages).
 */
import type { DataSensitivity, McpAccessLevel } from '../../../types';

// ---------------------------------------------------------------------------
// Enum option lists (verbatim C# enum member order)
// ---------------------------------------------------------------------------

export const MCP_ACCESS_LEVEL_OPTIONS: McpAccessLevel[] = ['None', 'ReadOnly', 'ReadWrite', 'Full'];
export const DATA_SENSITIVITY_OPTIONS: DataSensitivity[] = [
  'None',
  'Public',
  'Internal',
  'Confidential',
  'Restricted',
  'Pii',
];

// ---------------------------------------------------------------------------
// Validation Matrix — Register Server (`VS-`) — one function = one rule, verbatim error messages
// matching the server's validation contract where known.
// ---------------------------------------------------------------------------

/** VS-01 — Server Name, required @ submit. */
export function serverNameRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Server name is required';
}
/** VS-02 — Server Name, max 200 chars @ blur. */
export function serverNameMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Server name must be 200 characters or fewer' : undefined;
}

/** VS-03 — Server URL, required @ submit. */
export function serverUrlRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Server URL is required';
}
/** VS-04 — Server URL, max 1000 chars @ blur. */
export function serverUrlMaxLengthError(value: string): string | undefined {
  return value.length > 1000 ? 'Server URL must be 1000 characters or fewer' : undefined;
}
/** VS-05 — Server URL, must be a valid absolute http/https URL @ blur. */
export function serverUrlFormatError(value: string): string | undefined {
  if (!value.trim()) return undefined; // required-ness is VS-03's job
  try {
    const parsed = new URL(value);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return 'Server URL must be a valid absolute URL';
    }
    return undefined;
  } catch {
    return 'Server URL must be a valid absolute URL';
  }
}

/** VS-06 — Owner Id, max 200 chars @ blur (optional field). INFERRED provenance — the server
 *  contract states no explicit max; matches the Agent Registry's Owner Id bound for consistency. */
export function ownerIdMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Owner id must be 200 characters or fewer' : undefined;
}

/** VS-07 — Owner Team, max 200 chars @ blur (optional field). INFERRED provenance. */
export function ownerTeamMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Owner team must be 200 characters or fewer' : undefined;
}

/** VS-08 — Environment, max 100 chars @ blur (optional, free-text field — unlike the Agent
 *  Registry's Environment, MCP servers carry a free-text environment, not an enum). INFERRED
 *  provenance. */
export function environmentMaxLengthError(value: string): string | undefined {
  return value.length > 100 ? 'Environment must be 100 characters or fewer' : undefined;
}

/** VS-09 — Network Access Level, one of McpAccessLevel @ submit. */
export function networkAccessLevelRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a network access level';
}

/** VS-10 — Filesystem Access Level, one of McpAccessLevel @ submit. */
export function filesystemAccessLevelRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a filesystem access level';
}

/** VS-11 / VT-04 — Data Access Level, one of DataSensitivity @ submit. Shared by both the
 *  Register Server and Register Tool forms — both bind the same `dataAccessLevel: DataSensitivity`
 *  field, so one function serves both rule tables. */
export function dataSensitivityRequiredError(value: string): string | undefined {
  return value ? undefined : 'Select a data access level';
}

/** VS-12 — Metadata (JSON), valid JSON when non-empty @ blur. Shared by both forms (both accept
 *  an optional `metadataJson`). */
export function metadataJsonError(value: string): string | undefined {
  if (!value.trim()) return undefined;
  try {
    JSON.parse(value);
    return undefined;
  } catch {
    return 'Metadata must be valid JSON';
  }
}

// ---------------------------------------------------------------------------
// Validation Matrix — Register Tool (`VT-`)
// ---------------------------------------------------------------------------

/** VT-01 — Tool Name, required @ submit. */
export function toolNameRequiredError(value: string): string | undefined {
  return value.trim() ? undefined : 'Tool name is required';
}
/** VT-02 — Tool Name, max 200 chars @ blur. */
export function toolNameMaxLengthError(value: string): string | undefined {
  return value.length > 200 ? 'Tool name must be 200 characters or fewer' : undefined;
}

/** VT-03 — Description, max 2000 chars @ blur (optional field). */
export function descriptionMaxLengthError(value: string): string | undefined {
  return value.length > 2000 ? 'Description must be 2000 characters or fewer' : undefined;
}

/** VT-05 — Input Schema (JSON), valid JSON when non-empty @ blur. */
export function inputSchemaJsonError(value: string): string | undefined {
  if (!value.trim()) return undefined;
  try {
    JSON.parse(value);
    return undefined;
  } catch {
    return 'Input schema must be valid JSON';
  }
}

/** VT-06 — Output Schema (JSON), valid JSON when non-empty @ blur. */
export function outputSchemaJsonError(value: string): string | undefined {
  if (!value.trim()) return undefined;
  try {
    JSON.parse(value);
    return undefined;
  } catch {
    return 'Output schema must be valid JSON';
  }
}

// ---------------------------------------------------------------------------
// Server-error mapping — LOCAL COPY of the F031 Agent Registry helper of the same name
// (`agentFormFields.tsx`). Maps `Result` failure payloads (camelCase OR PascalCase field keys —
// the API has no camelCase JSON contract resolver today, see api.ts `fetchWithAuth`) onto the
// matching field's error state; anything unmapped becomes the form-level MessageBar text.
// ---------------------------------------------------------------------------

export type FieldErrorMap = Record<string, string>;

interface ServerErrorShape {
  message?: string;
  details?: Record<string, string[] | string>;
}

export function mapServerErrorToFormState(
  error: unknown,
  knownFields: readonly string[]
): { fieldErrors: FieldErrorMap; globalMessage?: string } {
  const fieldErrors: FieldErrorMap = {};
  let globalMessage: string | undefined;

  const shaped = error as ServerErrorShape;
  const details = shaped?.details;
  if (details && typeof details === 'object') {
    for (const [key, val] of Object.entries(details)) {
      // Newtonsoft's default PascalCase output (`ServerName`) -> camelCase field key (`serverName`).
      const camelKey = key.length > 0 ? key.charAt(0).toLowerCase() + key.slice(1) : key;
      const message = Array.isArray(val) ? val.join(' ') : String(val);
      if (knownFields.includes(camelKey)) {
        fieldErrors[camelKey] = message;
      } else {
        globalMessage = globalMessage ? `${globalMessage} ${message}` : message;
      }
    }
  }

  if (Object.keys(fieldErrors).length === 0 && !globalMessage) {
    globalMessage = error instanceof Error ? error.message : 'An unexpected error occurred.';
  }

  return { fieldErrors, globalMessage };
}
